import type { Conversation, PartialConversation } from '../types';

type ParticipantIdentity =
    | { __typename: 'Professional'; Title: string }
    | { __typename: 'Member'; Name: string; FirstName: string };

/**
 * Get the participants of a chat, other than the current user.
 * @param conversation The conversation to get the participants from.
 * @param userID The ID of the current user.
 * @returns The participants of the chat, other than the current user.
 */
export function getChatParticipants(
    conversation: Conversation | PartialConversation | undefined,
    userID: number
) {
    if (!conversation) {
        return [];
    }
    if (conversation.__typename === 'EnquiryConversation') {
        return [conversation.InitialEnquiry.Professional];
    }
    return conversation.Participants.filter(
        ({ Identity }) =>
            Identity.__typename !== 'Member' || Identity.ID !== userID
    ).map(({ Identity }) => Identity);
}

/**
 * Generate the title of a chat based on the chat title and participants.
 * @param conversation The conversation to get the title for.
 * @param otherParticipants The participants of the chat, other than the current user.
 * @returns The title of the chat.
 */
export function getChatTitle(
    conversation: Conversation | PartialConversation | undefined,
    otherParticipants: ParticipantIdentity[]
): string {
    if (!conversation) {
        return '';
    }
    if (conversation.__typename === 'EnquiryConversation') {
        return conversation.InitialEnquiry.Professional.Title;
    }
    if (conversation.Name) {
        return conversation.Name;
    }
    if (otherParticipants.length === 1) {
        const participant = otherParticipants[0]!;
        return participant.__typename === 'Professional'
            ? participant.Title
            : participant.Name;
    }
    return otherParticipants
        .map((participant) => {
            return participant.__typename === 'Professional'
                ? participant.Title
                : participant.FirstName;
        })
        .join(', ');
}
